import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {NgModule, LOCALE_ID, APP_INITIALIZER, Injector} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {AppComponent} from './app.component';
import {Http, HttpModule, XHRBackend} from '@angular/http';
import {HttpClientModule, HttpClient} from '@angular/common/http';
import {JsonpModule} from '@angular/http';
import {Router, RouterModule} from '@angular/router';
import {BootstrapModalModule} from 'ng2-bootstrap-modal';
import {LocalStorageModule} from 'angular-2-local-storage';
import {SimpleNotificationsModule} from 'angular2-notifications';

import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {NgxDatatableModule} from '@swimlane/ngx-datatable';
import {ApiService} from './services/api.service';
import {HomeModule} from './home/home.module';
import {ReportsModule} from './reports/reports.module';
import {AuthGuard} from './guards/loggedin.guard';
import {AdminGuard} from './guards/admin.guard';
import {routing} from './app.routing';
import {ProfileModule} from './profile/profile.module';
import {SupportedFormatsModule} from './supported-formats/supported-formats.module';
import {ErrorPageModule} from './error-page/error-page.module';
import {HttpService} from './services/interceptor';
import {ModalService} from './services/modalService/modal.service';
import {LoginComponent} from './account/login/login.component';
import {CreateComponent} from './account/create/create.component';
import {LoginService} from './services/login.service';
import {LoaderComponent} from './loader/loader.component';
import {LoaderService} from './loader/loader.service';
import {UtilsService} from './services/utils.service';
import {PrivacyStatementModule} from './privacy-statement/privacy-statement.module';
import {WhatIsLogModule} from './what-is-log/what-is-log.module';
import {ResetPasswordComponent} from './account/reset-password/reset-password.component';

import {AdminModule} from './admin/admin.module';
import {ReportGuard} from './guards/reports.guard';
import {DeactivateGuard} from './guards/deactivate.guard';
import {Ng2TableModule} from 'ng2-table';
import {ConfirmDialogComponent} from './confirm/confirm-dialog.component';
import {GenericModalComponent} from './generic-modal/generic-modal.component';
import {ConfirmDialogTermsComponent} from './account/confirmterms/confirmterms-dialog.component';
import {ParserLimitationInfoModalComponent} from './custom-modals/parser-limitation-info-modal.component';
import {VerifyYourEmailModalComponent} from './custom-modals/verify-your-email-modal.component';
import {LoseReportModalComponent} from './custom-modals/lose-report-modal.component';
import {ConfirmPaymentComponent} from './custom-modals/confirm-payment.component';
import {UnsubscribePaymentModalComponent} from './custom-modals/unsubscribe-payment-modal.component';
import {MessageResendModalComponent} from './custom-modals/message-resend-modal.component';
import {VerifyYourEmailValidationModalComponent} from './custom-modals/verify-your-email-validation-modal.component';
import {UnsubscribedUserModalComponent} from './custom-modals/unsubscribed-user-modal.component';
import {MigrationNoticeModalComponent} from './custom-modals/migration-notice-modal.component';
import {SignupNoticeModalComponent} from './custom-modals/signup-notice-modal.component';
import {LoginNoticeModalComponent} from './custom-modals/login-notice-modal.component';
import {ModalHelper} from './services/modalService/modal.helper';
import {PaypalModule} from './paypal/paypal.module';
import {EventService} from './services/event.service';
import {PaypalService} from './services/paypal.service';
import { NotificationsService } from 'angular2-notifications';
import {GoogleAnalyticsService} from './services/google-analytics-service';

import {appInitializerFactory} from './services/lang-initializer';


import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import {SafeUrlPipe} from './services/pipe/safe.url.pipe';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export let httpService = (backend: XHRBackend, loaderService: LoaderService,
                          eventService: EventService) => {
  return new HttpService(backend, loaderService, eventService);
};
@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ConfirmDialogComponent,
    GenericModalComponent,
    ConfirmDialogTermsComponent,
    ParserLimitationInfoModalComponent,
    VerifyYourEmailModalComponent,
    LoseReportModalComponent,
    UnsubscribePaymentModalComponent,
    MessageResendModalComponent,
    VerifyYourEmailValidationModalComponent,
    UnsubscribedUserModalComponent,
    MigrationNoticeModalComponent,
    SignupNoticeModalComponent,
    LoginNoticeModalComponent,
    CreateComponent,
    LoaderComponent,
    ResetPasswordComponent,
    ConfirmPaymentComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpModule,
    HttpClientModule,
    JsonpModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    HomeModule,
    ReportsModule,
    PaypalModule,
    NgxDatatableModule,
    ProfileModule,
    SupportedFormatsModule,
    ErrorPageModule,
    PrivacyStatementModule,
    WhatIsLogModule,
    routing,
    BootstrapModalModule,
    AdminModule,
    Ng2TableModule,
    SimpleNotificationsModule.forRoot(),
    LocalStorageModule.withConfig({
      prefix: '',
      storageType: 'localStorage'
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    })
  ],
  providers: [
    ApiService,
    ModalService,
    ModalHelper,
    AuthGuard,
    AdminGuard,
    ReportGuard,
    DeactivateGuard,
    EventService,
    GoogleAnalyticsService,
    LoginService,
    LoaderService,
    PaypalService,
    UtilsService,
    SafeUrlPipe,
    NotificationsService,
    {
      provide: Http,
      useFactory: httpService,
      deps: [XHRBackend, LoaderService, EventService]
    },
    { provide: LOCALE_ID, useValue: localStorage.getItem('lang') || 'en'},
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, Injector],
      multi: true
    }
  ],
  bootstrap: [AppComponent],
  // Add entry components for bootstrap modal components
  entryComponents: [
    LoginComponent,
    ConfirmDialogComponent,
    GenericModalComponent,
    ConfirmDialogTermsComponent,
    ParserLimitationInfoModalComponent,
    VerifyYourEmailModalComponent,
    LoseReportModalComponent,
    UnsubscribePaymentModalComponent,
    UnsubscribedUserModalComponent,
    MessageResendModalComponent,
    VerifyYourEmailValidationModalComponent,
    MigrationNoticeModalComponent,
    SignupNoticeModalComponent,
    LoginNoticeModalComponent,
    CreateComponent,
    ResetPasswordComponent,
    ConfirmPaymentComponent
  ],
  // exports: [
    
  //   TranslateModule
  // ]
})
export class AppModule {
}



// WEBPACK FOOTER //
// C:/Work/Projects/OnlineParser/web2-ui/src/app/app.module.ts